import React, { useEffect, useRef } from "react";
import {
  FiCpu,
  FiGlobe,
  FiMonitor,
  FiPhone,
  FiUpload,
} from "react-icons/fi";
import CallCenterModal from "../call-center/listing/CallCenterModal";

const FIELD_CLASS =
  "w-full h-11 px-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 disabled:bg-slate-50 disabled:text-slate-400";
const LABEL_CLASS = "block text-[11px] font-medium text-slate-500 mb-1.5 uppercase tracking-wide";
const LOGO_ACCEPT = "image/png,image/jpeg,image/jpg,image/gif,image/webp";

const SectionCard = ({ title, description, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      {description ? <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{description}</p> : null}
    </div>
    <div className="space-y-3 p-4 sm:p-5">{children}</div>
  </section>
);

const FeatureSwitch = ({
  id,
  checked,
  onChange,
  disabled,
  title,
  description,
  icon: Icon,
  nested = false,
}) => (
  <label
    htmlFor={id}
    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition sm:px-4 ${
      nested ? "ml-0 sm:ml-3 border-slate-100 bg-slate-50/80" : "border-slate-200 bg-slate-50/50"
    } ${disabled ? "cursor-not-allowed opacity-60" : "hover:border-[#2B7FFF]/30 hover:bg-white"}`}
  >
    {Icon ? (
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          checked && !disabled ? "bg-[#2B7FFF]/10 text-[#2B7FFF]" : "bg-white text-slate-400"
        } border border-slate-200`}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
    ) : null}
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-slate-800">{title}</span>
      {description ? (
        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{description}</span>
      ) : null}
    </span>
    <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
      <input
        id={id}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span
        className="absolute inset-0 rounded-full bg-slate-200 transition peer-checked:bg-[#2B7FFF] peer-focus-visible:ring-2 peer-focus-visible:ring-[#2B7FFF]/40 peer-disabled:opacity-50"
        aria-hidden="true"
      />
      <span
        className="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
        aria-hidden="true"
      />
    </span>
  </label>
);

const OrganizationFormModal = ({
  open,
  mode,
  formCode,
  formName,
  formActive,
  formCallingEnabled,
  formWebCallingEnabled,
  formDesktopCallingEnabled,
  formAiCallingEnabled,
  telephonyProvider,
  logoPreviewUrl,
  logoUploading,
  saving,
  onClose,
  onSave,
  onFormCodeChange,
  onFormNameChange,
  onFormActiveChange,
  onFormCallingEnabledChange,
  onFormWebCallingEnabledChange,
  onFormDesktopCallingEnabledChange,
  onFormAiCallingEnabledChange,
  onLogoUpload,
  t,
}) => {
  const codeInputRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = window.setTimeout(() => {
      if (mode === "create") {
        codeInputRef.current?.focus();
      } else {
        nameInputRef.current?.focus();
      }
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [open, mode]);

  const isCreate = mode === "create";
  const channelsDisabled = !formCallingEnabled || saving;

  const formatProvider = (code) => {
    if (!code) return t("ccOrgTelephonyProviderUnset");
    return String(code).replace(/_/g, " ");
  };

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      blockClose={saving}
      closeLabel={t("ccClose")}
      ariaLabelledBy="organization-form-title"
      eyebrow={isCreate ? t("ccOrgCreateTitle") : t("ccOrgEditTitle")}
      title={isCreate ? t("ccOrgCreateTitle") : formName || formCode}
      subtitle={isCreate ? t("ccOrgCreateFormHint") : t("ccOrgEditFormHint")}
      maxWidth="2xl"
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 space-y-4"
      headerBelow={
        !isCreate ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs font-semibold text-slate-700">
              {formCode}
            </span>
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                formActive
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/80"
                  : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
              }`}
            >
              {formActive ? t("ccActive") : t("ccInactive")}
            </span>
            {telephonyProvider ? (
              <span className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 ring-1 ring-indigo-200/80">
                {formatProvider(telephonyProvider)}
              </span>
            ) : null}
          </div>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-10 rounded-xl border border-slate-200 px-5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {t("ccCancel")}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="h-10 rounded-xl bg-[#2B7FFF] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1d6ef2] disabled:opacity-50"
          >
            {saving ? t("ccSaving") : isCreate ? t("ccOrgCreate") : t("ccSaveChanges")}
          </button>
        </>
      }
    >
      <h3 id="organization-form-title" className="sr-only">
        {isCreate ? t("ccOrgCreateTitle") : t("ccOrgEditTitle")}
      </h3>
            <SectionCard title={t("ccOrgSectionIdentity")} description={t("ccOrgSectionIdentityHint")}>
              {isCreate ? (
                <div>
                  <label className={LABEL_CLASS} htmlFor="organization-code">
                    {t("ccOrgCode")}
                    <span className="text-red-500"> *</span>
                  </label>
                  <input
                    id="organization-code"
                    ref={codeInputRef}
                    value={formCode}
                    onChange={(event) => onFormCodeChange(event.target.value.toUpperCase())}
                    className={`${FIELD_CLASS} font-mono uppercase`}
                    placeholder={t("ccOrgCodePlaceholder")}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={saving}
                  />
                  <p className="mt-1.5 text-xs text-slate-500">{t("ccOrgCodeHint")}</p>
                </div>
              ) : null}

              <div>
                <label className={LABEL_CLASS} htmlFor="organization-name">
                  {t("ccOrgName")}
                  <span className="text-red-500"> *</span>
                </label>
                <input
                  id="organization-name"
                  ref={nameInputRef}
                  value={formName}
                  onChange={(event) => onFormNameChange(event.target.value)}
                  className={FIELD_CLASS}
                  placeholder={t("ccOrgNamePlaceholder")}
                  disabled={saving}
                />
              </div>

              {!isCreate ? (
                <FeatureSwitch
                  id="organization-active"
                  checked={formActive}
                  onChange={onFormActiveChange}
                  disabled={saving}
                  title={t("ccOrgActive")}
                  description={t("ccOrgActiveHint")}
                />
              ) : null}
            </SectionCard>

            <SectionCard
              title={t("ccOrgCallingFeatures")}
              description={t("ccOrgCallingChannelsHint")}
            >
              <FeatureSwitch
                id="organization-calling"
                checked={formCallingEnabled}
                onChange={onFormCallingEnabledChange}
                disabled={saving}
                icon={FiPhone}
                title={t("ccOrgCallingEnabled")}
                description={t("ccOrgCallingMasterHint")}
              />

              <div
                className={`space-y-2 border-l-2 pl-3 sm:pl-4 ${
                  formCallingEnabled ? "border-[#2B7FFF]/25" : "border-slate-200"
                }`}
              >
                <FeatureSwitch
                  id="organization-web-calling"
                  nested
                  checked={formWebCallingEnabled}
                  onChange={onFormWebCallingEnabledChange}
                  disabled={channelsDisabled}
                  icon={FiGlobe}
                  title={t("ccOrgWebCallingEnabled")}
                  description={t("ccOrgWebCallingHint")}
                />
                <FeatureSwitch
                  id="organization-desktop-calling"
                  nested
                  checked={formDesktopCallingEnabled}
                  onChange={onFormDesktopCallingEnabledChange}
                  disabled={channelsDisabled}
                  icon={FiMonitor}
                  title={t("ccOrgDesktopCallingEnabled")}
                  description={t("ccOrgDesktopCallingHint")}
                />
                <FeatureSwitch
                  id="organization-ai-calling"
                  nested
                  checked={formAiCallingEnabled}
                  onChange={onFormAiCallingEnabledChange}
                  disabled={channelsDisabled}
                  icon={FiCpu}
                  title={t("ccOrgAiCallingEnabled")}
                  description={t("ccOrgAiCallingHint")}
                />
              </div>

              {!formCallingEnabled ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 ring-1 ring-amber-200/80">
                  {t("ccOrgCallingMasterOffNote")}
                </p>
              ) : null}
            </SectionCard>

            {!isCreate ? (
              <SectionCard title={t("ccOrgSectionBranding")} description={t("ccOrgLogoHint")}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-white">
                    {logoPreviewUrl ? (
                      <img
                        src={logoPreviewUrl}
                        alt={t("ccOrgLogo")}
                        className="max-h-full max-w-full object-contain p-2"
                      />
                    ) : (
                      <span className="px-3 text-center text-xs text-slate-400">{t("ccOrgNoLogo")}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#2B7FFF]/40 hover:bg-slate-50">
                      <FiUpload className="h-4 w-4 text-slate-500" aria-hidden="true" />
                      <input
                        type="file"
                        accept={LOGO_ACCEPT}
                        className="hidden"
                        disabled={logoUploading || saving}
                        onChange={onLogoUpload}
                      />
                      {logoUploading ? t("ccOrgLogoUploading") : t("ccOrgLogoUpload")}
                    </label>
                    <p className="mt-2 text-xs text-slate-500">{t("ccOrgLogoFormatsHint")}</p>
                  </div>
                </div>
              </SectionCard>
            ) : null}
    </CallCenterModal>
  );
};

export default OrganizationFormModal;
